import type { FlightFaresCopy } from '@voll-intelligence/intelligence-ui-kit';
import { flightPolicyReasons } from './flight_policy_reasons.js';
import { flightOfferCopy } from './flight_offer_copy.js';
import { genUiI18n } from './i18n.js';

/** Os textos da escolha de tarifa do kit no locale do host. Função pura, como
 *  o `flightOfferCopy`: o host memoiza por locale. */
export function flightFaresCopy(locale: string | null | undefined): FlightFaresCopy {
  const { t, locale: resolved } = genUiI18n(locale);
  const legWord = (leg: 'outbound' | 'return') =>
    t(leg === 'outbound' ? 'flightOffers.outbound' : 'flightOffers.return');
  return {
    offer: flightOfferCopy(locale),
    title: t('flightFares.title'),
    titleShort: t('flightFares.titleShort'),
    selectedFlights: t('flightFares.selectedFlights'),
    back: t('flightFares.back'),
    changeFlight: t('flightFares.changeFlight'),
    changeFlightAria: (leg) => t('flightFares.changeFlightAria', { leg }),
    faresOf: (leg) => t('flightFares.faresOf', { leg }),
    outbound: t('flightOffers.outbound'),
    return: t('flightOffers.return'),
    bothLegs: t('flightOffers.bothLegs'),
    faresBundled: t('flightOffers.faresBundled'),
    perTraveler: t('flightFares.perTraveler'),
    selected: t('flightFares.selected'),
    select: t('flightFilter.selectOffer'),
    railPrevious: (group) => t('flightFares.railPrevious', { group }),
    railNext: (group) => t('flightFares.railNext', { group }),
    slide: (position, total) => t('flightFares.slideAria', { position, total }),
    next: t('flightFares.next'),
    checkout: t('flightFares.checkout'),
    confirm: t('flightExplorer.confirm'),
    total: t('common.total'),
    policyCeilingAbove: (price) => t('flightExplorer.policyCeilingAbove', { price }),
    policyCeilingOut: t('flightExplorer.policyCeilingOut'),
    policyReason: (violations) => {
      const reasons = flightPolicyReasons(violations, t, resolved);
      return reasons.length > 0
        ? t('flightFares.policyReason', { reasons: reasons.join('; ') })
        : undefined;
    },
    airportSwitch: {
      title: t('flightFares.airportSwitch.title'),
      body: (arrival, departure) => t('flightFares.airportSwitch.body', { arrival, departure }),
      ack: t('flightFares.airportSwitch.ack'),
    },
    recommended: t('flightOffers.recommended'),
    recommendedWhy: {
      checked: t('fareSelector.recommendedWhy.checked'),
      cheapest: t('fareSelector.recommendedWhy.cheapest'),
    },
    supplierNotes: t('fareCard.supplierNotes'),
    curatedRules: t('fareCard.curatedRules'),
    rows: {
      carry: { yes: t('fareCard.carryYes'), no: t('fareCard.carryNo') },
      checked: { yes: t('fareCard.checkedYes'), no: t('fareCard.checkedNo') },
      seat: { yes: t('fareCard.seatYes'), no: t('fareCard.seatNo') },
      change: { yes: t('fareCard.changeYes'), no: t('fareCard.changeNo') },
      refund: { yes: t('fareCard.refundYes'), no: t('fareCard.refundNo') },
    },
    fee: (benefit) => t('fareCard.fee', { benefit }),
    unknown: (benefit) => t('fareCard.unknown', { benefit }),
    allowance: (benefit, count, weight) => {
      const kg = weight?.toLocaleString(resolved);
      if (count === null) return t('fareCard.allowanceKg', { benefit, kg });
      const one = count === 1;
      if (kg === undefined) {
        return t(one ? 'fareCard.allowancePieces_one' : 'fareCard.allowancePieces_other', {
          benefit,
          count,
        });
      }
      return t(one ? 'fareCard.allowancePiecesKg_one' : 'fareCard.allowancePiecesKg_other', {
        benefit,
        count,
        kg,
      });
    },
    // O artigo é interpolado porque com multi-trecho o rótulo deixa de ter gênero
    // previsível (`a ida`, `a volta`, mas `o trecho 2`); hoje as duas pernas do
    // aéreo são femininas. A chave sai do KIND: "sem despacho" e "despacha
    // mediante taxa" são avisos diferentes.
    asymmetric: (kind, leg) =>
      t(kind === 'fee' ? 'flightOffers.asymmetricBaggageFee' : 'flightOffers.asymmetricBaggage', {
        article: t('flightOffers.legArticleF'),
        leg: legWord(leg).toLowerCase(),
      }),
    loadingFares: t('flightOffers.loadingFares'),
    faresError: t('flightOffers.faresError'),
    faresStale: t('flightOffers.faresStale'),
    faresLegUnavailable: (leg) =>
      t(
        leg === 'outbound'
          ? 'flightOffers.faresLegUnavailableOutbound'
          : 'flightOffers.faresLegUnavailableReturn',
      ),
    searchAgain: t('flightOffers.searchAgain'),
    searchingAgain: t('flightOffers.searchingAgain'),
    tryAgain: t('common.tryAgain'),
  };
}
